import { paths } from "@kwp/firestore";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useClients } from "../../lib/clients";
import { useMe } from "../../lib/me";
import { toApproval, type Approval } from "./model";

export type ApprovalsTab = "pending" | "history";

export type ApprovalsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; approvals: Approval[] };

const DECIDED = ["approved", "rejected", "expired", "failed"] as const;
/** History shows the newest decisions (composite index ownerUid + status + createdAt). */
const HISTORY_PAGE = 50;

/**
 * The caller's approvals, live from Firestore (the rules let an owner read their own; changes go
 * through the api). Pending ones are few, so they are sorted here, newest first.
 */
export function useApprovals(tab: ApprovalsTab): ApprovalsState {
  const { firebase } = useClients();
  const me = useMe();
  const [state, setState] = useState<ApprovalsState>({ status: "loading" });

  useEffect(() => {
    setState({ status: "loading" });
    const col = collection(firebase.db, paths.approvals());
    const q =
      tab === "pending"
        ? query(col, where("ownerUid", "==", me.uid), where("status", "==", "pending"))
        : query(col, where("ownerUid", "==", me.uid), where("status", "in", [...DECIDED]), orderBy("createdAt", "desc"), limit(HISTORY_PAGE));
    return onSnapshot(
      q,
      (snap) => {
        const approvals = snap.docs.flatMap((d) => {
          const a = toApproval(d.id, d.data());
          return a === null ? [] : [a];
        });
        approvals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setState({ status: "ready", approvals });
      },
      (error) => setState({ status: "error", message: error.message }),
    );
  }, [firebase.db, me.uid, tab]);

  return state;
}

/** The current time, refreshed every minute so expiry labels and the badge stay right. */
export function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}
